import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ChatKeyOption } from '../../services/command-conversation.service';
import { ReasoningEffort } from '../../services/chat-execution';

export interface ComposerEffortOption { value: ReasoningEffort; label: string; }
export const composerEffortPlaceholder: readonly ComposerEffortOption[] = [
  { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' }, { value: 'xhigh', label: 'Extra high' },
  { value: 'max', label: 'Max' },
];

export type ComposerConnection = 'codex' | 'claude' | 'api';
export interface ComposerModelOption {
  id: string;
  connection: ComposerConnection;
  label: string | null;
  /** Provider model id; omitted for UI-only choices without an execution path. */
  model?: string;
  /** UI catalog; backend capability discovery is not connected yet. */
  efforts?: readonly ComposerEffortOption[];
  defaultEffort?: ReasoningEffort;
}

@Component({
  selector: 'app-composer-model-options',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="connections" role="group" aria-label="Connection">
      <button *ngFor="let connection of connections" type="button" class="br-interactive connection"
              [attr.aria-pressed]="browsing === connection.id" (click)="selectConnection(connection.id)">
        {{ connection.label }}
      </button>
    </div>
    <label class="models" *ngIf="browsing === 'api'">
      <span class="caption">API key</span>
      <select class="br-form-select br-form-select--sm" aria-label="API key"
              [ngModel]="selectedKeyId" (ngModelChange)="keySelected.emit($event)">
        <option [ngValue]="null">Company default</option>
        <option *ngIf="missingSelectedKey" [ngValue]="selectedKeyId">Unavailable key · {{ selectedKeyId }}</option>
        <option *ngFor="let key of keys" [ngValue]="key.id">{{ key.provider }} · Key {{ key.id }}</option>
      </select>
    </label>
    <label class="models">
      <span class="caption">Model</span>
      <select class="br-form-select br-form-select--sm" aria-label="Model"
              [ngModel]="selectedModel?.id" (ngModelChange)="chooseModel($event)">
        <option *ngFor="let model of visibleModels; trackBy: trackModel" [value]="model.id">
          {{ model.label || 'Default' }}
        </option>
      </select>
    </label>
    <ng-container *ngIf="selectedModel">
      <div class="effort">
        <div class="effort-header"><span>Effort</span><span>{{ effortLabel || '—' }}</span></div>
        <input type="range" min="0"
               [max]="effortLevels.length - 1" step="1" [value]="effortIndex"
               (input)="chooseEffort($event)" aria-label="Reasoning effort" [attr.aria-valuetext]="effortLabel">
      </div>
    </ng-container>
  `,
  styleUrls: ['./composer-model-options.component.css'],
})
export class ComposerModelOptionsComponent implements OnChanges {
  @Input() models: readonly ComposerModelOption[] = [];
  @Input() selectedId = 'codex';
  @Input() keys: readonly ChatKeyOption[] = [];
  @Input() selectedKeyId: number | null = null;
  @Output() keySelected = new EventEmitter<number | null>();
  @Input() selectedEffort: ReasoningEffort | null = null;
  @Output() modelSelected = new EventEmitter<ComposerModelOption>();
  @Output() effortSelected = new EventEmitter<ReasoningEffort>();
  readonly connections: readonly { id: ComposerConnection; label: string }[] = [
    { id: 'codex', label: 'Codex' }, { id: 'claude', label: 'Claude' }, { id: 'api', label: 'API' },
  ];
  get missingSelectedKey(): boolean { return typeof this.selectedKeyId === 'number' && !this.keys.some(key => key.id === this.selectedKeyId); }
  browsing: ComposerConnection = 'codex';
  visibleModels: readonly ComposerModelOption[] = [];
  get selectedModel(): ComposerModelOption | undefined { return this.visibleModels.find(model => model.id === this.selectedId) ?? this.visibleModels[0]; }
  get effortLevels(): readonly ComposerEffortOption[] { return this.selectedModel?.efforts?.length ? this.selectedModel.efforts : composerEffortPlaceholder; }
  get effort(): ComposerEffortOption {
    return this.effortLevels.find(option => option.value === this.selectedEffort)
      ?? this.effortLevels.find(option => option.value === (this.selectedModel?.defaultEffort ?? 'high'))
      ?? this.effortLevels[0];
  }
  get effortLabel(): string { return this.effort.label; }
  get effortIndex(): number { return this.effortLevels.indexOf(this.effort); }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedId'] || changes['models']?.firstChange) this.browsing = this.models.find(model => model.id === this.selectedId)?.connection ?? 'codex';
    this.visibleModels = this.models.filter(model => model.connection === this.browsing);
  }
  selectConnection(connection: ComposerConnection): void {
    this.browsing = connection;
    this.visibleModels = this.models.filter(model => model.connection === connection);
    const model = this.selectedModel;
    if (model) this.modelSelected.emit(model);
  }
  chooseModel(id: string): void {
    const model = this.visibleModels.find(option => option.id === id);
    if (model) this.modelSelected.emit(model);
  }
  chooseEffort(event: Event): void {
    const level = this.effortLevels[Number((event.target as HTMLInputElement).value)];
    if (level) this.effortSelected.emit(level.value);
  }
  trackModel(_index: number, model: ComposerModelOption): string { return model.id; }
}
